import { AsyncPipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DrillCowListComponent } from 'src/app/shared/components/drill-cow-list/drill-cow-list.component';
import { Production } from 'src/app/shared/models/production.model';

import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';
import { Group } from '../../models/fertility.model';
import * as FertilityActions from '../../store/actions/fertility-actions';
import { getFertilitySummary } from '../../store/selectors/fertility-selectors';

@Component({
  selector: 'app-fertility-summary',
  templateUrl: './fertility-summary.component.html',
  styleUrls: ['./fertility-summary.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NotificationEmptyComponent,
    AsyncPipe,
    DecimalPipe,
    TranslatePipe,
  ],
})
export class FertilitySummaryComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly dialogService = inject(DialogService);

  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public columns: {
    id: string;
    label: string;
    type: 'string' | 'value';
  }[] = [
    { id: 'cowGroupName', label: 'fertility.fertility-summary.cow-group-name', type: 'string' },
    {
      id: 'cowQuarter',
      label: 'fertility.fertility-summary.cow-quarter',
      type: 'value',
    },
    {
      id: 'cowHalfYear',
      label: 'fertility.fertility-summary.cow-half-year',
      type: 'value',
    },
    { id: 'cowYear', label: 'fertility.fertility-summary.cow-year', type: 'value' },
    { id: 'calfGroupName', label: 'fertility.fertility-summary.calf-group-name', type: 'string' },
    {
      id: 'calfQuarter',
      label: 'fertility.fertility-summary.calf-quarter',
      type: 'value',
    },
    {
      id: 'calfHalfYear',
      label: 'fertility.fertility-summary.calf-half-year',
      type: 'value',
    },
    { id: 'calfYear', label: 'fertility.fertility-summary.calf-year', type: 'value' },
  ];
  public dataSource = new MatTableDataSource<FertilitySummaryTable>();

  private getFertilitySummary$ = this.store.select(getFertilitySummary);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'fertility.fertility-summary.page-title' }),
    );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onProductionChanged(production: Production): void {
    this.store.dispatch(
      FertilityActions.loadFertilitySummaryRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
    this.getFertilitySummary$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        console.log('Done and done' + result.calfGroups?.length);
        this.dataSource.data = [
          this.mapToTable(
            result.cowGroups.find((c) => c.name === 'ExpectedTimeBetweenCalving'),
            '1.0-0',
            result.calfGroups.find((c) => c.name === 'ExpectedCalvingAge'),
            '1.1-1',
          ),
          this.mapToTable(
            result.cowGroups.find((c) => c.name === 'TimeBetweenCalving'),
            '1.0-0',
            result.calfGroups.find((c) => c.name === 'CalvingAgeHeiffers'),
            '1.1-1',
          ),
          this.mapToTable(
            result.cowGroups.find((c) => c.name === 'IntervalBetweenFirstInseminationAndCalving'),
            '1.0-0',
            result.calfGroups.find(
              (c) => c.name === 'AverageNumberOfInseminationsPerCowYoungStock',
            ),
            '1.2-2',
          ),
          this.mapToTable(
            result.cowGroups.find((c) => c.name === 'AverageNumberOfInseminationsPerCow'),
            '1.2-2',
            result.calfGroups.find((c) => c.name === 'PercentageNonReturningAfter56DaysYoungStock'),
            '1.2-2',
          ),
          this.mapToTable(
            result.cowGroups.find((c) => c.name === 'PercentageNonReturningAfter56Days'),
            '1.2-2',
            null,
            '1.0-0',
          ),
        ];
      });
  }

  public columnIds = (): string[] => this.columns.map((item) => item.id);
  public hasData = () => this.dataSource?.data?.length > 0;
  public openDrillCowList(cowsList: string[]) {
    const cows = [...cowsList].sort().map((cow) => ({ internationalCowId: cow, emphasize: true }));

    this.dialogService
      .fullscreenDialog(DrillCowListComponent, {
        cows,
        title: this.localeService.translate('fertility.fertility-summary.page-title'),
        setPendingChanges: false,
        fullscreen: true,
      })
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          // Handle the confirmation status but do nothing
        }
      });
  }
  private mapToTable = (
    cowGroup: Group,
    cowFormat: string,
    calfGroup: Group,
    calfFormat: string,
  ): FertilitySummaryTable => ({
    cowGroupName: this.localeService.translate(
      `fertility.fertility-summary.cow-group-name-${cowGroup?.name}`,
    ),
    cowQuarter: {
      value: cowGroup?.quarterValue,
      count: cowGroup?.quarterCount,
      format: cowFormat,
      list: cowGroup?.quarterCows,
    },
    cowHalfYear: {
      value: cowGroup?.halfYearValue,
      count: cowGroup?.halfYearCount,
      format: cowFormat,
      list: cowGroup?.halfYearCows,
    },
    cowYear: {
      value: cowGroup?.yearValue,
      count: cowGroup?.yearCount,
      format: cowFormat,
      list: cowGroup?.yearCows,
    },
    calfGroupName:
      calfGroup !== null
        ? this.localeService.translate(
            `fertility.fertility-summary.calf-group-name-${calfGroup?.name}`,
          )
        : '',
    calfQuarter: {
      value: calfGroup?.quarterValue,
      count: calfGroup?.quarterCount,
      format: calfFormat,
      list: calfGroup?.quarterCows,
    },
    calfHalfYear: {
      value: calfGroup?.halfYearValue,
      count: calfGroup?.halfYearCount,
      format: calfFormat,
      list: calfGroup?.halfYearCows,
    },
    calfYear: {
      value: calfGroup?.yearValue,
      count: calfGroup?.yearCount,
      format: calfFormat,
      list: calfGroup?.yearCows,
    },
  });
}

export interface FertilitySummaryCell {
  value: number;
  count: number;
  format: string;
  list: string[];
}
export interface FertilitySummaryTable {
  cowGroupName: string;
  cowQuarter: FertilitySummaryCell;
  cowHalfYear: FertilitySummaryCell;
  cowYear: FertilitySummaryCell;
  calfGroupName: string;
  calfQuarter: FertilitySummaryCell;
  calfHalfYear: FertilitySummaryCell;
  calfYear: FertilitySummaryCell;
}
